import React, { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { cx } from './util';

/**
 * Button — the mockups' .primary / .act / .ghost / .danger / .dangerb.
 *
 * variant: 'act' (default secondary) | 'primary' | 'quiet' (.ghost) |
 *          'danger' (outlined red) | 'halt' (solid red, confirm dialogs) |
 *          'flatten' (Flatten all) | 'link' (inline text button)
 * size:    'sm' (34 px) | 'md' (default 38/40 px) | 'lg' (42 px)
 * iconOnly: square 40 px (the ••• button). Give it an aria-label.
 * to / href: renders a router <Link> / <a> styled as a button.
 * busy: disables and shows `busyLabel` (defaults to the children).
 */
const Button = forwardRef(function Button(
  { variant = 'act', size = 'md', iconOnly = false, block = false, busy = false, busyLabel, to, href, className, disabled, type = 'button', children, ...rest },
  ref,
) {
  const cls = cx(
    'ui-btn',
    `ui-btn--${variant}`,
    size === 'sm' && 'ui-btn--sm',
    size === 'lg' && 'ui-btn--lg',
    iconOnly && 'ui-btn--icon',
    block && 'ui-btn--block',
    className,
  );
  const content = busy && busyLabel ? busyLabel : children;
  const inert = Boolean(disabled || busy);
  // A disabled link must not navigate (an <a> has no disabled attribute).
  const guard = (e) => {
    if (inert) { e.preventDefault(); return; }
    if (rest.onClick) rest.onClick(e);
  };
  if (to) {
    return (
      <Link ref={ref} to={to} className={cls} aria-disabled={inert || undefined} tabIndex={inert ? -1 : rest.tabIndex} {...rest} onClick={guard}>
        {content}
      </Link>
    );
  }
  if (href) {
    return (
      <a ref={ref} href={href} className={cls} aria-disabled={inert || undefined} tabIndex={inert ? -1 : rest.tabIndex} {...rest} onClick={guard}>
        {content}
      </a>
    );
  }
  return (
    <button ref={ref} type={type} className={cls} disabled={disabled || busy} aria-busy={busy || undefined} {...rest}>
      {content}
    </button>
  );
});

export default Button;
